/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Divider,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import UsagePeriodsEditor from './UsagePeriodsEditor';
import { toUsagePayload, toUsageRows, validateUsageRows } from '../../utils/usagePeriods';

/**
 * Edit one resource assignment on a project: rate, quantity and usage dates.
 * `assignment` is an entry from GET /api/resources/assignments/project/:id.
 * Once any quantity is invoiced the rate is locked and the quantity cannot drop
 * below what was billed; usage dates stay editable (tracking only).
 */
const ResourceAssignmentEditDialog = ({ open, onClose, projectId, assignment, onSaved }) => {
  const [rateId, setRateId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [usageRows, setUsageRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && assignment) {
      setRateId(assignment.rate_id || '');
      setQuantity(String(assignment.quantity ?? ''));
      setUsageRows(toUsageRows(assignment.usage_periods));
    }
  }, [open, assignment]);

  if (!assignment) return null;

  const rates = assignment.resource?.unit?.rates || [];
  const invoicedQty = assignment.invoiced_quantity || 0;
  const unitLabel = assignment.resource?.unit?.unit_short || assignment.resource?.unit?.base_unit || '';

  const handleSave = async () => {
    const q = parseFloat(String(quantity).trim());
    if (isNaN(q) || q <= 0 || q < invoicedQty) {
      toast.error(`Enter a quantity greater than 0${invoicedQty > 0 ? ` and not less than the ${invoicedQty} already invoiced` : ''}.`);
      return;
    }
    if (rates.length > 0 && !rateId) {
      toast.error('Please select a rate');
      return;
    }
    const usageError = validateUsageRows(usageRows);
    if (usageError) {
      toast.error(usageError);
      return;
    }
    try {
      setSaving(true);
      await axios.put(`/api/resources/assignments/project/${projectId}/${assignment._id}`, {
        quantity: q,
        rate_id: rateId,
        usage_periods: toUsagePayload(usageRows)
      });
      toast.success('Resource assignment updated');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update resource assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Resource — {assignment.resource?.resource || 'Resource'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
          <FormControl size="small" sx={{ flex: 2, minWidth: 200 }}>
            <InputLabel>Rate</InputLabel>
            <Select
              value={rateId}
              label="Rate"
              onChange={(e) => setRateId(e.target.value)}
              disabled={invoicedQty > 0 || rates.length === 0}
            >
              {rates.map(r => (
                <MenuItem key={r.rate_id} value={r.rate_id}>
                  {r.display_name} - ${Number(r.rate || 0).toFixed(2)} / {r.rate_basis || 'unit'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={`Quantity${unitLabel ? ` (${unitLabel})` : ''}`}
            type="number"
            size="small"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ min: invoicedQty || 0, step: 'any' }}
            sx={{ flex: 1, minWidth: 120 }}
          />
        </Box>
        {invoicedQty > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {invoicedQty} already invoiced: the rate is locked and the quantity cannot go below {invoicedQty}.
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2">Usage dates</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Add one row per run, as many as needed. For tracking only; the resource is billed by quantity.
        </Typography>
        <UsagePeriodsEditor rows={usageRows} onChange={setUsageRows} disabled={saving} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResourceAssignmentEditDialog;
